import React, { useState, useRef, useEffect } from 'react';
import { Download, FileSpreadsheet, FileText, Loader2, ChevronDown } from 'lucide-react';

/**
 * ExportMenu — one-click "Export → Excel / PDF" dropdown used by
 * Customers, Orders and Transactions pages. Replaces the legacy
 * Excel-import buttons that lived in the same toolbar slot.
 *
 * Props:
 *   url           string — full export endpoint, including query string
 *                          (parent already encodes its filter state).
 *                          Format parameter is appended by this component.
 *   token         string — auth bearer.
 *   filename      string — base name; `.xlsx` / `.pdf` appended at download.
 *   testid        string — root data-testid (default 'export-menu').
 *   disabled      boolean — disables the trigger when true.
 *   label         string — button label, default 'Export'.
 */
export default function ExportMenu({ url, token, filename = 'export', testid = 'export-menu', disabled = false, label = 'Export' }) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const download = async (format) => {
    setOpen(false);
    setDownloading(true);
    try {
      const sep = url.includes('?') ? '&' : '?';
      const res = await fetch(`${url}${sep}format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const tx = await res.text();
        throw new Error(tx || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `${filename}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      alert('Export failed: ' + (e?.message || String(e)));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="relative inline-flex" ref={wrapRef} data-testid={testid}>
      <button
        type="button"
        disabled={disabled || downloading}
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-trigger`}
        className={`px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all text-sm ${
          disabled || downloading
            ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
            : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg'
        }`}
      >
        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        <span className="hidden sm:inline">{downloading ? 'Exporting…' : label}</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-80" />
      </button>
      {open && !downloading && (
        <div
          className="absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden"
          data-testid={`${testid}-list`}
        >
          <button
            onClick={() => download('excel')}
            data-testid={`${testid}-xlsx`}
            className="w-full px-3 py-2 flex items-center gap-2 text-sm text-slate-700 hover:bg-slate-50 text-left"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Excel (.xlsx)
          </button>
          <button
            onClick={() => download('pdf')}
            data-testid={`${testid}-pdf`}
            className="w-full px-3 py-2 flex items-center gap-2 text-sm text-slate-700 hover:bg-slate-50 text-left border-t border-slate-100"
          >
            <FileText className="w-4 h-4 text-rose-600" /> PDF (.pdf)
          </button>
        </div>
      )}
    </div>
  );
}
